import { useState, useEffect, useRef } from "react";
import { Bell, Megaphone, User, MessageCircle, Mail, ChevronRight, X, Calendar, HeartHandshake } from "lucide-react";
import { api, addWsListener } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";

export default function NotificationBell() {
  const [count, setCount] = useState(0);
  const [notifs, setNotifs] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setCount(data.count);
      setNotifs(data.notifications);
    } catch {}
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);

    // WS Listener for instant refresh
    const removeListener = addWsListener((msg) => {
      if (msg.type === "notification_refresh") {
        load();
      }
    });

    return () => {
      clearInterval(interval);
      removeListener();
    };
  }, []);

  const toggle = () => {
    if (!open) {
      load();
      api.post("/notifications/read").catch(() => {});
      setCount(0);
    }
    setOpen(!open);
  };

  const getIcon = (type) => {
    switch (type) {
      case 'announcement': return <Megaphone className="w-4 h-4 text-blue-500" />;
      case 'profile': return <User className="w-4 h-4 text-red-500" />;
      case 'chat': return <MessageCircle className="w-4 h-4 text-green-500" />;
      case 'dm': return <Mail className="w-4 h-4 text-purple-500" />;
      case 'event': return <Calendar className="w-4 h-4 text-amber-500" />;
      case 'prayer': return <HeartHandshake className="w-4 h-4 text-indigo-500" />;
      default: return <Bell className="w-4 h-4 text-gray-500" />;
    }
  };

  const clickNotif = (path) => {
    setOpen(false);
    // Convert mobile path to web path if needed
    let webPath = path;
    if (path === "/profile") webPath = "/profile";
    if (path === "/announcements") webPath = "/#announcements";
    if (path === "/groups") webPath = "/admin";
    if (path === "/dm") webPath = "/messages";

    if (webPath.startsWith("/#")) {
      const id = webPath.substring(2);
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth" });
      else navigate("/");
    } else {
      navigate(webPath);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggle}
        className="p-2 rounded-full hover:bg-black/5 relative transition-colors"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-[#1C201E]" />
        {count > 0 && (
          <span className="absolute top-1.5 right-1.5 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white">
            {count > 9 ? '9+' : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-2 w-80 bg-white border border-[#E5E0D8] rounded-2xl shadow-xl z-50 overflow-hidden"
            >
              <div className="px-4 py-3 border-b border-[#E5E0D8] flex items-center justify-between bg-[#FAF9F6]">
                <h3 className="font-bold text-[#1C201E] text-sm">Notifications</h3>
                <button onClick={() => setOpen(false)} className="text-[#5C6661] hover:text-[#1C201E]">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="max-h-96 overflow-auto">
                {notifs.length === 0 ? (
                  <div className="p-8 text-center text-[#5C6661]">
                    <Bell className="w-8 h-8 mx-auto mb-2 opacity-20" />
                    <p className="text-xs">No new notifications</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#E5E0D8]">
                    {notifs.map((n, i) => (
                      <button
                        key={i}
                        onClick={() => clickNotif(n.path)}
                        className="w-full text-left p-4 hover:bg-[#F3EFEA] transition-colors flex items-start gap-3 group"
                      >
                        <div className="mt-0.5 p-2 rounded-lg bg-[#FAF9F6] border border-[#E5E0D8]">
                          {getIcon(n.type)}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-bold text-[#1C201E] leading-tight group-hover:text-[#2C4C3B]">{n.title}</div>
                          <div className="text-xs text-[#5C6661] mt-1 leading-relaxed">{n.body}</div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#E5E0D8] mt-1" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {notifs.length > 0 && (
                <div className="p-3 bg-[#FAF9F6] border-t border-[#E5E0D8] text-center">
                  <span className="text-[10px] uppercase tracking-widest text-[#8A8F8B]">BBC Connect Alerts</span>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
